import type { BadgeProps } from "@shopify/polaris"

import {
  ActionButton,
  IProcessStatus,
  IReturnStatus,
  MoreAction,
} from "@/models/IManagement"

import {
  RETURN_SHIPPING_METHOD_LIST,
  RETURN_SHIPPING_METHOD_MAP,
  RETURN_SOLUTION_MAP,
} from "./basis"
import { SHOPFIYCOUNTRYLIST } from "./shopfiyCountryList"

const STATUS_MAP = () => ({
  [IReturnStatus.PendingReview]: {
    label: t("PendingReview0"),
    status: "default",
  },
  [IReturnStatus.Approved]: {
    label: t("Approved"),
    status: "info",
  },
  [IReturnStatus.Resolved]: {
    label: t("Resolved"),
    status: "success",
  },
  [IReturnStatus.Rejected]: {
    label: t("Rejected"),
    status: "critical",
  },
  [IReturnStatus.Archived]: {
    label: t("Archived"),
    status: "default",
  },
})

const PROCESS_STATUS_MAP = (): Record<
  IProcessStatus,
  {
    label: string
  } & BadgeProps
> => ({
  [IProcessStatus.Refunded]: {
    label: t("Refunded"),
    tone: "success",
  },
  [IProcessStatus.Refund_failed]: {
    label: t("RefundFailed"),
    tone: "critical",
  },
  [IProcessStatus.Exchanged]: {
    label: t("Exchanged"),
    tone: "success",
  },
  [IProcessStatus.Refund_Processing]: {
    label: t("RefundProcessing"),
    tone: "info",
  },
  [IProcessStatus.Pending_Refund]: {
    label: t("PendingRefund0"),
    tone: "attention",
  },
  [IProcessStatus.Pending_Exchange]: {
    label: "Pending exchange",
    tone: "attention",
  },
  [IProcessStatus.Received]: {
    label: t("Received"),
    progress: "complete",
  },
  [IProcessStatus.Restocked]: {
    label: t("Restocked"),
    progress: "complete",
  },
  [IProcessStatus.Partially_Received]: {
    label: t("PartiallyReceived"),
    progress: "partiallyComplete",
  },
  [IProcessStatus.Partially_Restocked]: {
    label: t("PartiallyRestocked"),
    progress: "partiallyComplete",
  },
})

const MORE_ACTION_MAP = () => ({
  [MoreAction.Received]: {
    label: t("MarkAsReceived"),
    id: "received",
    path: "received_modal_show",
  },
  [MoreAction.Restock]: {
    label: t("MarkAsRestock"),
    id: "restock",
    path: "restock_modal_show",
  },
})

const BUTTON_SHOW_MAP = () => ({
  [ActionButton.Approved]: {
    label: t("Approve"),
    primary: true,
    id: "approved",
    path: "approved_modal_show",
  },
  [ActionButton.Exchange]: {
    label: t("CreateExchangeOrder"),
    primary: true,
    id: "exchange",
    path: "exchange_modal_show",
  },
  [ActionButton.exchanged_diff]: {
    label: t("CreateExchangeOrder"),
    primary: true,
    id: "exchange",
    path: "exchange_modal_show",
  },
  [ActionButton.Received]: {
    label: t("MarkAsReceived"),
    primary: false,
    id: "received",
    path: "received_modal_show",
  },
  [ActionButton.Refund]: {
    label: t("Refund"),
    primary: true,
    id: "refunded",
    path: "refund_modal_show",
  },
  [ActionButton.Rejected]: {
    label: t("Reject"),
    primary: false,
    tone: "critical" as const,
    id: "rejected",
    path: "rejected_modal_show",
  },
  [ActionButton.Resolved]: {
    label: t("MarkAsResolved"),
    primary: true,
    id: "resolved",
    path: "resolved_modal_show",
  },
  [ActionButton.Restock]: {
    label: t("Restored"),
    primary: false,
    id: "resolved",
    path: "restock_modal_show",
  },
})

interface ListParams {
  list: any[]
  type: "solution" | "shipping_method"
  value?: "value" | "id"
  label?: "label" | "name"
}
const tlist = ({
  list,
  type,
  value = "value",
  label = "label",
}: ListParams) => {
  const fnMap = {
    solution: RETURN_SOLUTION_MAP,
    shipping_method: RETURN_SHIPPING_METHOD_MAP,
  } as const

  const sourceList =
    type === "shipping_method"
      ? RETURN_SHIPPING_METHOD_LIST.map((id) =>
          list.find((item) => item.id === id),
        ).filter(Boolean)
      : list

  return sourceList.map((item) => {
    const realValue = item[value]
    const mapped = fnMap[type]()[realValue]
    return {
      ...item,
      [label]: mapped?.customLabel ?? mapped?.label ?? item[label],
    }
  })
}

const EXPERTSUPPORT_OPTIONS = () => [
  {
    label: t("Demo"),
    value: "demo",
  },
  {
    label: t("WhatsApp"),
    value: "whatsapp",
  },
  {
    label: t("Slack"),
    value: "slack",
  },
  {
    label: t("WeCom"),
    value: "wecom",
  },
]

const INTEGRATIONGLS_PACKAPGETYPE_OPTIONS = () => [
  { label: t("Sack"), value: 7 },
  { label: t("Colli"), value: 1 },
  { label: t("Box"), value: 2 },
  { label: t("Roll"), value: 3 },
  { label: t("Can"), value: 4 },
  { label: t("Case"), value: 5 },
  { label: t("Reel"), value: 6 },
]

// 退款状态 0-等待退款 1-退款中 2-退款失败  3-退款成功
const REFUND_STATUS_TEXT_MAP = () => ({
  0: t("PendingRefund0"),
  1: t("RefundProcessing"),
  2: t("RefundFailed"),
  3: t("Refunded"),
})

export {
  BUTTON_SHOW_MAP,
  EXPERTSUPPORT_OPTIONS,
  INTEGRATIONGLS_PACKAPGETYPE_OPTIONS,
  MORE_ACTION_MAP,
  PROCESS_STATUS_MAP,
  REFUND_STATUS_TEXT_MAP,
  RETURN_SHIPPING_METHOD_MAP,
  RETURN_SOLUTION_MAP,
  SHOPFIYCOUNTRYLIST,
  STATUS_MAP,
  tlist,
}
export * from "./basis"
export * from "./plan"
export * from "./workflow"
